import { useQuery } from '@tanstack/react-query';
import { portalAPI } from '@/lib/api/portal';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn, formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Check, Users, Calendar, MapPin } from 'lucide-react';

interface TournamentSelectionStepProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onComplete: () => void;
  onBack: () => void;
}

export function TournamentSelectionStep({
  selectedIds,
  onChange,
  onComplete,
  onBack,
}: TournamentSelectionStepProps) {
  const { data: tournamentsData, isLoading } = useQuery({
    queryKey: ['tournaments', { status: 'open', visible: true }],
    queryFn: () => portalAPI.getTournaments({ status: 'open', visible: true }),
  });

  const tournaments = tournamentsData?.docs || [];

  const toggleTournament = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(tid => tid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading tournaments...</div>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-muted-foreground">No tournaments available at this time.</p>
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={onComplete}>Skip & Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold">Select Tournaments</h2>
        <p className="text-muted-foreground mt-2">
          Choose which tournaments you'd like to compete in
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {tournaments.map(tournament => {
          const isSelected = selectedIds.includes(tournament.id);
          const available = tournament.maxParticipants - tournament.currentParticipants - tournament.reservedParticipants;
          const isFull = available <= 0;

          return (
            <Card
              key={tournament.id}
              className={cn(
                'cursor-pointer transition-all',
                isSelected ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50',
                isFull && 'opacity-60 cursor-not-allowed'
              )}
              onClick={() => !isFull && toggleTournament(tournament.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{tournament.name}</CardTitle>
                    <CardDescription>
                      {tournament.weapon && <span className="capitalize">{tournament.weapon}</span>}
                      {tournament.division && <span> • {tournament.division}</span>}
                    </CardDescription>
                  </div>
                  <div className={cn(
                    'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                  )}>
                    {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(tournament.date)}
                  </div>
                  <div className="flex items-center gap-1">
                    {formatTime(tournament.startTime)}
                  </div>
                </div>
                {tournament.location && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {tournament.location}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm">
                    <Users className="w-4 h-4" />
                    <span className={cn(isFull ? 'text-destructive' : 'text-muted-foreground')}>
                      {isFull ? 'Full' : `${available} spots left`}
                    </span>
                  </div>
                  <div className="font-semibold">
                    {formatCurrency(tournament.registrationFee)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onComplete}>
          {selectedIds.length > 0 ? `Continue with ${selectedIds.length} tournament${selectedIds.length > 1 ? 's' : ''}` : 'Skip & Continue'}
        </Button>
      </div>
    </div>
  );
}
